import React, { FC, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiMove } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { PSButton } from '@/components/ui/button';

import { useAppSelector } from '../../../app/hook';
import { useLazyGetScenarioDetailQuery, useModifyScenarioMutation } from '../../../app/services/scenario-rtk.service';
import { AlertsType } from '../../../constants/alerts.constant';
import { BlockType } from '../../../interfaces/models/Knowledge/scenario.interface';
import MyOverlayTrigger from '../../common/MyOverlayTrigger';
import useMacBotActions from './slice/useMacBotActions';

interface ITree {
  id: string;
  type: number;
  title: string;
  parent: any;
  message: string;
  block_id: string;
  childrens: Childrens[];
  children: Children[];
}

interface Childrens {
  id: string;
  type: BlockType;
}

interface Children {
  next: any;
  type: number;
  title: string;
  label: string;
  parent: string;
  media?: any;
  block_id: string;
  id: string;
  childrens: Childrens[];
  children: Children[];
}

type Props = {
  tree: ITree[];
  currentId: string;
};

const NodeParentChanger: FC<Props> = ({ tree, currentId }) => {
  const { t } = useTranslation();
  const currentScenarioId = useAppSelector(state => state.bots.wizard.id);

  const [modifyScenario, modifyScenarioStatus] = useModifyScenarioMutation();
  const [getScenario] = useLazyGetScenarioDetailQuery();
  const [showModal, setShowModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>('');

  const { closeForm } = useMacBotActions(currentScenarioId);
  const getNodesWithEmptyChildrens = (tree: ITree[], currentId: string) => {
    const result: any[] = []; // To store the valid last leaf nodes
    const visited = new Set(); // To avoid duplicates
    const skipSet = new Set(); // To track all nodes in the subtree of currentId

    // Helper function to recursively check for leaf nodes
    function checkChildren(node: any) {
      // Skip the node if it's part of the subtree of currentId
      if (skipSet.has(node.id || node.parent)) {
        return [];
      }

      // Skip this node entirely if it's the currentId node or type 20 (end of conversation, go to, external link (video and text))
      const skipTypes = new Set([
        BlockType.EndOFConversation,
        BlockType.Goto,
        BlockType.VideoGoto,
        BlockType.VideoLink,
        BlockType.TextLink,
        BlockType.PassToAi,
        BlockType.SwitchToChatbot,
      ]);

      const shouldSkipNode = node.id === currentId || node.parent === currentId || skipTypes.has(node.type);

      if (shouldSkipNode) {
        // Mark all ancestors of this node (subtree) for skipping
        let currentNode = node;
        while (currentNode) {
          skipSet.add(currentNode.id);
          currentNode = tree.find(n => n.parent === currentNode.id);
        }
        return []; // Prevent further processing
      }

      // If the node has no children (leaf node)
      if (!node.children || node.children.length === 0) {
        return [node]; // Return an array with the single leaf node
      }

      const validLeaves: any[] = []; // Array to store all valid leaves for this node

      // Recursively check the 'children' property (if exists)
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => {
          const childLeaves = checkChildren(child); // Recurse into the child
          validLeaves.push(...childLeaves); // Add all valid leaves from the child
        });
      }

      return validLeaves; // Return an array of valid leaves for this branch
    }

    // Start checking the provided data
    for (const item of tree) {
      if (item.id === currentId) break; // Skip processing for currentId
      const validLeaves = checkChildren(item); // Get all valid leaves for this branch
      validLeaves.forEach(leaf => {
        if (!visited.has(leaf.id)) {
          const currentNode = tree.find(dr => dr.id === currentId);
          const isInvalidElse = currentNode && leaf.type === BlockType.ElseCondition && leaf.parent === currentNode.parent;

          if (!isInvalidElse) {
            visited.add(leaf.id); // Avoid duplicates
            result.push(leaf); // Add the valid leaf node to the result
          }
        }
      });
    }

    // Return all valid last leaf nodes
    return result;
  };

  const handleChangeNode = () => {
    if (selectedOption === '') return;

    modifyScenario({
      scenarioId: `${currentScenarioId}`,
      data: {
        single: {
          action: 'displacement',
          data: {
            id: currentId,
            parent: selectedOption,
          },
        },
      },
    })
      .unwrap()
      .then(() => {
        closeForm();
        toast.success(t(AlertsType.ISKBKO004), { delay: 100 });
        setShowModal(false);
        getScenario(currentScenarioId);
      })
      .catch(res => {
        if (res.status == 403) {
          toast.error(t(AlertsType.ISGLOK001), {
            delay: 100,
          });
        } else {
          if (res.status == 400) {
            toast.warn(res.data.message || res.message || t(AlertsType.ISKBKO013), { delay: 100 });
          } else {
            toast.error(res.data.message || res.message || t(AlertsType.ISKBKO013), { delay: 100 });
          }
        }
      });
  };

  return (
    <React.Fragment>
      <MyOverlayTrigger title={t('NodeParentChanger/key5')} placement="top">
        <PSButton
          id="btn-move-node"
          variant="info"
          onClick={() => setShowModal(true)}
          type="button"
          icon={<BiMove size={24} className="tw-w-6 tw-h-6 tw-relative tw-overflow-hidden" />}
        />
      </MyOverlayTrigger>

      {/* Modal for edit block parent */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>{t('NodeParentChanger/key1')}</Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label htmlFor="change-node">{t('NodeParentChanger/key2')}</Form.Label>
            <Form.Select translate="no" id="change-node" onChange={e => setSelectedOption(e.target.value)}>
              <option value="">{t('NodeParentChanger/key3')}</option>
              {getNodesWithEmptyChildrens(tree, currentId).map(item => (
                <option key={item.id} value={item.id}>
                  {item.block_id} - {item.title || item.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <PSButton
            id="btn-change-node"
            className="tw-rounded-full"
            disabled={modifyScenarioStatus.isLoading}
            onClick={handleChangeNode}
            title={t('NodeParentChanger/key4')}
          />
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default NodeParentChanger;
